import * as React from 'react';
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Paper from '@mui/material/Paper';
import { IoIosArrowDown } from 'react-icons/io';
import Checkbox from '@mui/material/Checkbox';
import Input from '../atoms/Input';
import Button from '../atoms/Button';
import './Table.styles.css';
import MenuDropdown from '../atoms/MenuDropdown';
import isMobile from '../../utils/isMobile';

export interface BaseData {
  id: string;
  [key: string]: string | number | JSX.Element;
}

interface HeadCell<T extends BaseData> {
  id: keyof T;
  label: string;
}

interface EnhancedTableProps<T extends BaseData> {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof T
  ) => void;
  allowSelect?: boolean;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: keyof T;
  rowCount: number;
  headCells: readonly HeadCell<T>[];
  actions: boolean;
  hasToolbar: boolean;
}

// Function to normalize strings by removing accents
const normalizeString = (str: string): string => str
  .toLowerCase()
  .normalize('NFD') // Decompose accented characters
  .replace(/[\u0300-\u036f]/g, ''); // Remove diacritical marks

// Improved text extraction function
const extractTextFromJSX = (element: React.ReactNode): string => {
  if (element === null || element === undefined) {
    return '';
  }

  // Handle primitive types directly
  if (typeof element === 'string' || typeof element === 'number' || typeof element === 'boolean') {
    return String(element);
  }

  // Handle arrays (like children arrays)
  if (Array.isArray(element)) {
    return element.map(extractTextFromJSX).join(' ');
  }

  // Handle React elements
  if (React.isValidElement(element)) {
    // Extract text from children
    const childrenText = extractTextFromJSX(element.props.children);
    return childrenText;
  }

  // Handle objects that might be fragments or other special React types
  if (typeof element === 'object') {
    return '';
  }

  return '';
};

const parsePotentiallyNumericString = (str: string): number | null => {
  const cleanedStr = str.replace(/[$,€£¥%\s]/g, '').trim();

  if (cleanedStr === '') {
    return null;
  }

  const hasDot = cleanedStr.includes('.');
  const hasComma = cleanedStr.includes(',');

  let parsableStr = cleanedStr;

  if (hasDot && hasComma) {
    if (cleanedStr.lastIndexOf(',') > cleanedStr.lastIndexOf('.')) {
      parsableStr = cleanedStr.replace(/\./g, '').replace(',', '.');
    } else {
      parsableStr = cleanedStr.replace(/,/g, '');
    }
  } else if (hasDot) {
    const parts = cleanedStr.split('.');
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) {
      parsableStr = cleanedStr.replace(/\./g, '');
    }
  } else if (hasComma) {
    const parts = cleanedStr.split(',');
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) {
      parsableStr = cleanedStr.replace(/,/g, '');
    } else {
      parsableStr = cleanedStr.replace(',', '.');
    }
  }

  const num = parseFloat(parsableStr);
  return Number.isNaN(num) ? null : num;
};

const descendingComparator = <T extends BaseData>(
  a: T,
  b: T,
  orderBy: keyof T,
): number => {
  const valueA = React.isValidElement(a[orderBy]) ? extractTextFromJSX(a[orderBy]) : a[orderBy];
  const valueB = React.isValidElement(b[orderBy]) ? extractTextFromJSX(b[orderBy]) : b[orderBy];

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueB - valueA;
  }

  const strA = String(valueA);
  const strB = String(valueB);

  const numA = parsePotentiallyNumericString(strA);
  const numB = parsePotentiallyNumericString(strB);

  if (numA !== null && numB !== null) {
    return numB - numA;
  }

  const normalizedStrA = normalizeString(strA);
  const normalizedStrB = normalizeString(strB);

  if (normalizedStrB < normalizedStrA) {
    return -1;
  }
  if (normalizedStrB > normalizedStrA) {
    return 1;
  }
  return 0;
};

type Order = 'asc' | 'desc';

const getComparator = <T extends BaseData>(
  order: Order,
  orderBy: keyof T,
): ((a: T, b: T) => number) => (order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy));

function EnhancedTableHead<T extends BaseData>({
  allowSelect = false,
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  headCells,
  actions,
  hasToolbar,
}: EnhancedTableProps<T>) {
  const createSortHandler = (property: keyof T) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead
      sx={{
        '& .MuiTableRow-root > th:first-of-type': {
          borderTopLeftRadius: hasToolbar ? 0 : '24px',
        },
        '& .MuiTableRow-root > th:last-child': {
          borderTopRightRadius: hasToolbar ? 0 : '24px',
        },
      }}
    >
      <TableRow>
        {allowSelect && (
          <TableCell sx={{
            width: '30px',
            whiteSpace: 'nowrap',
            color: 'rgb(var(--color-text-secondary))',
            backgroundColor: 'rgb(var(--color-surface-secondary))',
          }}>
            {false && <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              sx={{
                padding: '0px',
              }}
              icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" fill="rgb(var(--color-surface-base))" />
                <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" stroke="rgb(var(--color-border-primary))" />
              </svg>
              }
              checkedIcon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="20" height="20" rx="6" fill="rgba(173, 23, 129, 1)" />
                <path d="M14.6666 6.5L8.24998 12.9167L5.33331 10" stroke="rgb(var(--color-text-inverse))" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>}
              indeterminateIcon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="20" height="20" rx="6" fill="rgba(173, 23, 129, 1)" />
                <path d="M5 10 H15" stroke="rgb(var(--color-text-inverse))" stroke-width="2" stroke-linecap="round" />
              </svg>}
            />
            }
          </TableCell>
        )}

        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id as React.Key}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              padding: '12px 12px',
              whiteSpace: 'nowrap',
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              sx={{
                color: 'rgb(var(--color-text-primary))',
                whiteSpace: 'nowrap',
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: 'rgb(var(--color-text-primary))',
                },
                '&:focus': {
                  color: 'rgb(var(--color-text-primary)) !important',
                },
                '&.Mui-active': {
                  color: 'rgb(var(--color-text-primary))',
                },
                '& .MuiTableSortLabel-icon': {
                  color: 'rgb(var(--color-text-secondary)) !important',
                },
                '&:hover .MuiTableSortLabel-icon': {
                  color: 'rgb(var(--color-text-primary)) !important',
                },
              }}
            >
              {headCell.label}
              {orderBy === headCell.id ? <div></div> : null}
            </TableSortLabel>
          </TableCell>
        ))}
        {actions && <TableCell sx={{ width: '80px', padding: '12px 12px' }}>Acciones</TableCell>}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = <T extends BaseData>({
  showFilter,
  showSearch,
  onSearch,
  searchQuery,
  customButtons,
  filterColumns,
  headCells,
  onToggleColumn,
  visibleColumns,
}: {
  showFilter?: boolean,
  headCells: readonly HeadCell<T>[],
  showSearch?: boolean,
  onSearch?: (query: string) => void,
  searchQuery?: string,
  customButtons?: JSX.Element[];
  loading?: boolean;
  filterColumns?: boolean;
  onToggleColumn?: (columnId: keyof T) => void;
  visibleColumns?: (keyof T)[];
}) => {
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => { onSearch?.(event.target.value); };
  const filterColumnsOptions = headCells.map((headCell) => ({
    label: headCell.label,
    value: headCell.id,
    selected: visibleColumns?.includes(headCell.id) || false,
  }));

  return showFilter || showSearch || customButtons || filterColumns ? (
    <Toolbar>
      <div className={`flex ${filterColumns && isMobile() ? 'flex-col' : 'flex-row'} w-full py-4 gap-4 items-center`}>
        {showSearch && (
          <div className="w-full">
            <Input
              id={'search'}
              label={'Buscar'}
              isSearch={true}
              onChange={handleSearch}
              value={searchQuery}
            />
          </div>
        )}

        {showFilter && (
          <div className={`flex items-center border border-border-primary rounded-3xl p-3 ${filterColumns && isMobile() ? 'w-full' : 'w-fit'} whitespace-nowrap`}>
            <div className='flex justify-between whitespace-nowrap items-center text-text-primary'>
              Filtrar
              <div className='text-2xl ml-auto'>
                <IoIosArrowDown />
              </div>
            </div>
          </div>
        )}

        {filterColumns && (
          <div className={`flex items-center border border-border-tertiary rounded-3xl p-2 ${filterColumns && isMobile() ? 'w-full' : 'w-fit'}`}>
            <MenuDropdown
              label='Columnas'
              icon={<IoIosArrowDown />}
              checkboxes
              data={filterColumnsOptions}
              options={filterColumnsOptions.map((option) => ({
                label: option.label,
                selected: option.selected,
                onClick: () => {
                  if (onToggleColumn) {
                    onToggleColumn(option.value as keyof T);
                  }
                },
              }))}
            />
          </div>
        )}

        {customButtons && customButtons.map((button, index) => (
          <div key={`custom-button-${index}`} className={`flex ${filterColumns && isMobile() ? 'w-full' : 'w-fit'} items-center`}>
            {button}
          </div>
        ))}

      </div>
    </Toolbar>) : null;
};

interface FilterTableProps<T extends BaseData> {
  rows: T[];
  headCells: readonly HeadCell<T>[];
  actions?: (row: T) => JSX.Element;
  pagination?: {
    pageSize?: number;
    enabled: boolean;
  }
  filtering?: {
    enabled: boolean;
  }
  searchEnabled?: boolean;
  allowSelect?: boolean;
  onSelectionChange?: (selectedIds: readonly string[]) => void;
  customButtons?: JSX.Element[];
  overflowX?: 'auto' | 'hidden';
  filterColumns?: boolean;
  tableId: string;
  highlightedRows?: boolean;
  /**
   * Changing this value will clear all current row selections.
   * Prefer passing a monotonically increasing number or a unique token when you want to reset.
   */
  clearSelectionKey?: string | number;
}

function FilterTable<T extends BaseData>({
  rows, headCells, actions, pagination, filtering, searchEnabled = true, allowSelect = false, onSelectionChange, customButtons, overflowX = 'auto', filterColumns, tableId, highlightedRows, clearSelectionKey,
}: FilterTableProps<T>) {
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof T>('');
  const [selected, setSelected] = React.useState<readonly string[]>([]);

  const storageKey = `tablePage_${tableId}`;

  const [page, setPage] = React.useState(() => {
    const storedPage = sessionStorage.getItem(storageKey);

    if (storedPage) {
      return parseInt(storedPage, 10);
    }
    return 0;
  });

  const [searchQuery, setSearchQuery] = React.useState('');
  const [rowsPerPage] = React.useState(pagination?.pageSize || 5);
  const [visibleColumns, setVisibleColumns] = React.useState<(keyof T)[]>(
    headCells.map((cell) => cell.id));

  const toggleColumnVisibility = (columnId: keyof T) => {
    setVisibleColumns((prevColumns) => {
      if (prevColumns.includes(columnId)) {
        return prevColumns.filter((id) => id !== columnId);
      }
      return [...prevColumns, columnId];
    });
  };

  const visibleHeadCells = React.useMemo(() => headCells.filter((cell) => visibleColumns.includes(cell.id)), [headCells, visibleColumns]);

  const visibleRows = React.useMemo(
    () => {
      if (rows.length === 0) {
        return [];
      }

      // Filter rows based on search query
      const normalizedSearchQuery = normalizeString(searchQuery);
      const filteredRows = searchQuery
        ? rows.filter((row) => Object.entries(row).some(([key, value]) => {
          // Skip the id field or any other fields you want to exclude
          if (key === 'id') { return false; }

          // For primitive values, search directly
          if (typeof value === 'string' || typeof value === 'number') {
            return normalizeString(String(value)).includes(normalizedSearchQuery);
          }

          // For JSX Elements, extract and search their text content
          if (React.isValidElement(value)) {
            const textContent = extractTextFromJSX(value);
            return normalizeString(textContent).includes(normalizedSearchQuery);
          }

          return false;
        }))
        : rows;

      const sortedRows = [...filteredRows].sort(getComparator(order, orderBy));
      return pagination?.enabled
        ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : sortedRows;
    },
    [order, orderBy, page, rowsPerPage, rows, pagination?.enabled, searchQuery],
  );

  const totalPages = React.useMemo(() => {
    const normalizedSearchQuery = normalizeString(searchQuery);
    const filteredRowCount = searchQuery
      ? rows.filter((row) => Object.entries(row).some(([key, value]) => {
        if (key === 'id') { return false; }

        if (typeof value === 'string' || typeof value === 'number') {
          return normalizeString(String(value)).includes(normalizedSearchQuery);
        }

        if (React.isValidElement(value)) {
          const textContent = extractTextFromJSX(value);
          return normalizeString(textContent).includes(normalizedSearchQuery);
        }

        return false;
      })).length
      : rows.length;
    return pagination?.enabled ? Math.ceil(filteredRowCount / rowsPerPage) : 1;
  }, [rows, searchQuery, pagination?.enabled, rowsPerPage]);

  useEffect(() => {
    if (totalPages < page + 1) {
      setPage(0);
    }
  }, [totalPages]);

  // Clear all selections only when clearSelectionKey changes
  useEffect(() => {
    if (clearSelectionKey !== undefined) {
      setSelected([]);
      onSelectionChange?.([]);
      // Also clear search input when resetting selection context
      setSearchQuery('');
    }
  }, [clearSelectionKey]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof T,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    const isDesc = orderBy === property && order === 'desc';

    if (isDesc) {
      setOrder('asc');
      setOrderBy('');
    } else {
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      onSelectionChange?.(newSelected);
      return;
    }
    setSelected([]);
    onSelectionChange?.([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
    onSelectionChange?.(newSelected);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
      sessionStorage.setItem(storageKey, newPage.toString());
    }
  };

  const getPageNumbers = (totalPageNumber: number, currentPage: number) => {
    const delta = 1;
    const pages: (number | string)[] = [];

    for (let i = 0; i < totalPageNumber; i += 1) {
      if (
        i === 0
        || i === totalPageNumber - 1
        || (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        pages.push(i);
      } else if (
        i === currentPage - delta - 1
        || i === currentPage + delta + 1
      ) {
        pages.push('...');
      }
    }

    return pages.filter((currPage, index, array) => currPage !== '...' || (currPage === '...' && array[index - 1] !== '...'));
  };

  return (
    <Box sx={{ width: '100%', color: 'rgb(var(--color-text-primary))' }}>
      <Paper sx={{
        width: '100%', boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)', border: '1px solid rgb(var(--color-border-secondary))', borderRadius: '24px', backgroundColor: 'rgb(var(--color-surface-base))', overflow: 'hidden',
      }}>
        <EnhancedTableToolbar
          showFilter={filtering?.enabled}
          showSearch={searchEnabled}
          onSearch={setSearchQuery}
          searchQuery={searchQuery}
          customButtons={customButtons}
          filterColumns={filterColumns}
          headCells={headCells}
          onToggleColumn={toggleColumnVisibility}
          visibleColumns={visibleColumns}
        />
        {visibleRows.length > 0 ? (
          <TableContainer
            sx={{
              overflowX: overflowX ?? 'auto',
              maxWidth: '100%',
              '@media (max-width: 600px)': {
                maxWidth: '100vw',
              },
              '&::-webkit-scrollbar': {
                height: '2px',
                width: '2px',
                display: 'block',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgb(var(--color-surface-secondary))',
                borderRadius: '24px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgb(var(--color-border-primary))',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgb(var(--color-text-tertiary))',
              },
            }}
          >
            <Table
              sx={{
                minWidth: '100%',
                '& .MuiTableCell-root': {
                  padding: '8px 20px',
                  borderBottom: '1px solid rgb(var(--color-border-secondary)) !important',
                },
                '& thead th': {
                  backgroundColor: 'rgb(var(--color-surface-secondary)) !important',
                  color: 'rgb(var(--color-text-primary))',
                  fontWeight: 600,
                  '&.MuiTableCell-root': {
                    color: 'rgb(var(--color-text-primary)) !important',
                  },
                },
                '& tbody td.MuiTableCell-root': {
                  color: 'rgb(var(--color-text-primary)) !important',
                },
              }}
              aria-labelledby="tableTitle"
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                allowSelect={allowSelect}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                headCells={visibleHeadCells}
                actions={!!actions}
                hasToolbar={Boolean(filtering?.enabled || searchEnabled || (customButtons && customButtons.length > 0) || filterColumns)}
              />
              <TableBody
                key={pagination?.enabled ? page : 'all'}
                sx={{
                  '& .MuiTableRow-root:nth-of-type(even)': {
                    backgroundColor: highlightedRows ? 'rgb(var(--color-surface-secondary)) !important' : 'transparent',
                  },
                }}
              >
                {visibleRows.map((row, index) => {
                  const isItemSelected = selected.includes(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  const rowKey = pagination?.enabled ? `${row.id}-${page}-${index}` : row.id;

                  return allowSelect ? (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={rowKey}
                      selected={isItemSelected}
                      sx={{
                        cursor: 'pointer',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(var(--color-selection), 0.1) !important',
                        },
                      }}
                    >
                      <TableCell sx={{
                        width: '30px',
                        whiteSpace: 'nowrap',
                      }}>
                        <Checkbox
                          color="primary"
                          sx={{
                            padding: '0px',
                          }}
                          checked={isItemSelected}
                          icon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" fill="rgb(var(--color-surface-base))" />
                            <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" stroke="rgb(var(--color-border-primary))" />
                          </svg>}
                          checkedIcon={<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="20" height="20" rx="6" fill="rgba(173, 23, 129, 1)" />
                            <path d="M14.6666 6.5L8.24998 12.9167L5.33331 10" stroke="rgb(var(--color-text-inverse))" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>}
                        />
                      </TableCell>
                      {visibleHeadCells.map((headCell) => (
                        <TableCell
                          key={headCell.id as React.Key}
                          component={headCell.id === visibleHeadCells[0].id ? 'th' : 'td'}
                          id={headCell.id === visibleHeadCells[0].id ? labelId : undefined}
                          sx={{
                            pading: '12px 12px',
                            minWidth: 'auto',
                            width: 'auto',
                            wordBreak: 'break-word',
                          }}
                        >
                          {row[headCell.id]}
                        </TableCell>
                      ))}
                      {actions && (
                        <TableCell sx={{ padding: '8px 12px' }}>
                          {actions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  ) : (
                    <TableRow
                      key={rowKey}
                      sx={{
                        color: 'rgb(var(--color-text-secondary))',
                      }}
                    >
                      {visibleHeadCells.map((headCell) => (
                        <TableCell
                          key={headCell.id as React.Key}
                          sx={{
                            padding: '12px 12px',
                            minWidth: 'auto',
                            width: 'auto',
                            wordBreak: 'break-word',
                          }}
                        >
                          {row[headCell.id]}
                        </TableCell>
                      ))}
                      {actions && (
                        <TableCell sx={{ padding: '12px 12px' }}>
                          {actions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow style={{ height: 53 }}>
                    <TableCell colSpan={visibleHeadCells.length + 1} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-full mt-4 mb-10">
            <h1 className="text-2xl font-medium text-text-secondary text-center">No se encontraron resultados</h1>
            <p className="text-text-tertiary text-center">Intenta ajustar tu búsqueda o filtros para encontrar lo que estás buscando</p>
          </div>
        )}
        {pagination?.enabled && rows.length > 0 && (
          <div className={`${visibleRows.length === 0 ? 'border-t border-border-primary' : ''}`}>
            <Box className="grid grid-cols-3 items-center gap-4 sm:gap-2 px-6 py-4 max-[830px]:hidden">
              <div className='w-fit'>
                <Button
                  variant="secondary"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 0}
                >
                  Anterior
                </Button>
              </div>

              <div className="flex flex-row items-center justify-center w-fit mx-auto gap-1">
                {getPageNumbers(totalPages, page).map((pageNum, index) => (
                  pageNum === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2 max-w-fit text-text-primary">...</span>
                  ) : (
                    <Button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum as number)}
                      variant='page'
                      isSelect={page === pageNum}
                    >
                      {(pageNum as number) + 1}
                    </Button>
                  )
                ))}
              </div>

              <div className='w-fit justify-self-end'>
                <Button
                  variant="secondary"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Siguiente
                </Button>
              </div>
            </Box>
            <div className="grid grid-cols-3 min-[830px]:hidden items-center justify-center h-full p-4">
              <Button variant="page" className="justify-self-start" onClick={() => handlePageChange(page - 1)} disabled={page === 0}>{'<'}</Button>
              <span className="text-center text-sm text-text-primary">{visibleRows.length === 0 ? '1 de 1' : `${page + 1} de ${totalPages}`}</span>
              <Button variant="page" className="justify-self-end" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages - 1}>{'>'}</Button>
            </div>
          </div>
        )}
      </Paper>
    </Box>
  );
}

export default FilterTable;
